import {
  capitalizeTerm,
  FORGE_MAX_ITEMS,
  FORGE_NAME_MAX,
  type ForgeItem,
  formatAmountWithUnit,
  parseAmount,
} from "@loot-os/api-contract";
import { isLootApiErrorLike } from "@loot-os/react";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { CheckIcon, XIcon } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { useCurrency } from "../../lib/currency.js";
import {
  contributionCents,
  cutPercentToBps,
  earningsPerOpenCents,
  FORGE_TOTAL_WEIGHT,
  type ForgeRow,
  forgeErrorMessage,
  forgeNameHint,
  forgeSubmitBlocker,
  forgeSubmitLabel,
  isPullRateInput,
  livePriceCents,
  sortRowsByValue,
  totalWeight,
  useCreateForgeBox,
  useUpdateForgeDropTable,
} from "../../lib/forge.js";
import { forgeImagesQueryOptions, forgeItemsInfiniteQueryOptions } from "../../lib/queries.js";
import { useTenant } from "../../lib/tenant.js";
import { useTerminology } from "../../lib/terminology.js";
import { useLoadMore } from "../../lib/useLoadMore.js";
import { ErrorBanner } from "../ErrorBanner.js";
import { UsernameDialog } from "../UsernameDialog.js";

/** What the edit page loads the builder with: the published version it edits. */
export interface ForgeBuilderEdit {
  boxId: string;
  name: string;
  imageUrl: string;
  version: number;
  creatorCutPercent: number;
  rows: ForgeRow[];
}

/**
 * The forge builder, boxed's five steps on one page: name, image, the items, their pull rates,
 * then the cut and the price. Editing (`edit` set) locks the name and image and sends the whole
 * table again, pinned to the version it loaded.
 */
export function ForgeBuilder({
  edit,
  onVersionChanged,
}: {
  edit?: ForgeBuilderEdit;
  /** Editing only: the box moved on under the builder; the page reloads it. */
  onVersionChanged?: () => void;
}) {
  const terms = useTerminology();
  const currency = useCurrency();
  const { config } = useTenant();
  const navigate = useNavigate();
  const forge = config.forge;
  const commission = forge?.commissionEnabled ?? false;
  const maxCut = forge?.maxCreatorCutPercent ?? 0;

  const images = useQuery({ ...forgeImagesQueryOptions(), enabled: !edit });
  const imageList = images.data?.images ?? [];

  const [name, setName] = useState(edit?.name ?? "");
  const [imageUrl, setImageUrl] = useState(edit?.imageUrl ?? "");
  const [rows, setRows] = useState<ForgeRow[]>(edit?.rows ?? []);
  const [cutPercent, setCutPercent] = useState(() =>
    commission ? Math.min(edit?.creatorCutPercent ?? 0, maxCut) : 0,
  );
  const [usernameOpen, setUsernameOpen] = useState(false);

  const create = useCreateForgeBox();
  const update = useUpdateForgeDropTable(edit?.boxId ?? "");
  const pending = create.isPending || update.isPending;

  const sentCut = commission ? cutPercent : 0;
  const cutBps = cutPercentToBps(sentCut);
  const priceCents = livePriceCents(rows, cutBps);
  const total = totalWeight(rows);
  const state = {
    mode: edit ? ("edit" as const) : ("create" as const),
    name,
    imageUrl,
    imagesOffered: imageList.length,
    rows,
  };
  const blocker = forgeSubmitBlocker(state);
  const selected = useMemo(() => new Set(rows.map((row) => row.id)), [rows]);

  function toggleItem(item: ForgeItem) {
    if (selected.has(item.id)) {
      setRows((prev) => prev.filter((row) => row.id !== item.id));
      return;
    }
    if (rows.length >= FORGE_MAX_ITEMS) {
      toast.error(`Up to ${FORGE_MAX_ITEMS} ${terms.item.plural} per ${terms.box.singular}.`);
      return;
    }
    setRows((prev) => [
      ...prev,
      {
        id: item.id,
        name: item.name,
        imageUrl: item.imageUrl,
        valueCents: item.valueCents,
        percent: "",
      },
    ]);
  }

  function setPercent(id: string, text: string) {
    if (!isPullRateInput(text)) return;
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, percent: text } : row)));
  }

  function submit() {
    if (blocker !== null || pending) return;
    const onError = (err: unknown) => {
      toast.error(
        forgeErrorMessage(err, {
          terms,
          currency,
          itemName: (id) => rows.find((row) => row.id === id)?.name,
        }),
      );
      if (!isLootApiErrorLike(err)) return;
      const code: string = err.code;
      if (code === "username_required") setUsernameOpen(true);
      if (code === "box_version_changed") onVersionChanged?.();
    };
    if (edit) {
      update.mutate(
        { creatorCutPercent: sentCut, rows, expectedVersion: edit.version },
        {
          onSuccess: () => {
            toast.success(`${capitalizeTerm(terms.box.singular)} saved.`);
            void navigate({ to: "/forge/mine" });
          },
          onError,
        },
      );
      return;
    }
    create.mutate(
      { name, imageUrl: imageList.length > 0 ? imageUrl : "", creatorCutPercent: sentCut, rows },
      {
        onSuccess: (res) => {
          toast.success(`${capitalizeTerm(terms.box.singular)} forged.`);
          void navigate({ to: "/boxes/$boxId", params: { boxId: res.box.id } });
        },
        onError,
      },
    );
  }

  const nameHint = name.trim() ? forgeNameHint(name) : null;

  return (
    <div className="grid gap-6">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_26rem]">
        <div className="grid min-w-0 content-start gap-6">
          <Step number={1} title={`${capitalizeTerm(terms.box.singular)} name`}>
            {edit ? (
              <p className="text-sm font-medium">{edit.name}</p>
            ) : (
              <div className="grid gap-1.5">
                <Input
                  id="forge-name"
                  aria-label={`${capitalizeTerm(terms.box.singular)} name`}
                  value={name}
                  maxLength={FORGE_NAME_MAX}
                  placeholder={`Name your ${terms.box.singular}`}
                  onChange={(e) => setName(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Up to {FORGE_NAME_MAX} characters: letters, numbers and spaces. No profanity.
                </p>
                {nameHint && <p className="text-xs text-destructive">{nameHint}</p>}
              </div>
            )}
          </Step>

          {edit ? (
            edit.imageUrl ? (
              <Step number={2} title="Image">
                <BoxImage src={edit.imageUrl} alt="" className="size-24 rounded-lg object-cover" />
              </Step>
            ) : null
          ) : images.isPending ? (
            <Skeleton className="h-32 rounded-xl" />
          ) : imageList.length > 0 ? (
            <Step number={2} title="Image">
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
                {imageList.map((url) => (
                  <button
                    key={url}
                    type="button"
                    aria-pressed={imageUrl === url}
                    aria-label="Use this image"
                    onClick={() => setImageUrl(url)}
                    className={`relative aspect-square overflow-hidden rounded-lg border-2 bg-muted ${
                      imageUrl === url ? "border-primary" : "border-transparent hover:border-border"
                    }`}
                  >
                    <img src={url} alt="" className="size-full object-cover" />
                    {imageUrl === url && (
                      <span className="absolute top-1 right-1 rounded-full bg-primary p-0.5 text-primary-foreground">
                        <CheckIcon className="size-3" />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </Step>
          ) : null}

          <Step
            number={3}
            title={`Pick ${terms.item.plural}`}
            description={`${rows.length} of up to ${FORGE_MAX_ITEMS} selected.`}
          >
            <ItemPicker selected={selected} onToggle={toggleItem} />
          </Step>
        </div>

        <div className="grid min-w-0 content-start gap-6 lg:sticky lg:top-20 lg:self-start">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <div className="grid gap-1">
                <CardTitle className="text-sm">
                  <StepNumber n={4} /> Pull rates
                </CardTitle>
                <CardDescription>Each {terms.item.singular}'s chance, in percent.</CardDescription>
              </div>
              <span
                className={`font-mono text-base font-semibold tabular-nums ${
                  total === FORGE_TOTAL_WEIGHT
                    ? "text-emerald-500"
                    : total === 0
                      ? "text-muted-foreground"
                      : "text-destructive"
                }`}
              >
                {(total / 10_000).toFixed(3)}%
              </span>
            </CardHeader>
            <CardContent className="grid gap-2">
              {rows.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Pick at least two {terms.item.plural} to set their pull rates.
                </p>
              ) : (
                <>
                  <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
                    Sort by value
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setRows((prev) => sortRowsByValue(prev, "asc"))}
                    >
                      Low → high
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setRows((prev) => sortRowsByValue(prev, "desc"))}
                    >
                      High → low
                    </Button>
                  </div>
                  <div className="grid max-h-[28rem] gap-2 overflow-y-auto pr-1">
                    {rows.map((row) => (
                      <div
                        key={row.id}
                        className="grid grid-cols-[3rem_minmax(0,1fr)_auto] items-center gap-2 rounded-md border border-border p-2"
                      >
                        <BoxImage
                          src={row.imageUrl}
                          alt=""
                          className="size-12 rounded object-contain"
                        />
                        <div className="grid min-w-0 gap-1">
                          <span className="truncate text-xs font-medium">{row.name}</span>
                          <span className="font-mono text-[11px] text-muted-foreground">
                            {formatAmountWithUnit(row.valueCents, currency)}
                          </span>
                          <div className="flex items-center gap-2">
                            <div className="relative w-24">
                              <Input
                                inputMode="decimal"
                                aria-label={`Pull rate for ${row.name}`}
                                placeholder="0.000"
                                value={row.percent}
                                onChange={(e) => setPercent(row.id, e.target.value)}
                                className="h-8 pr-6 font-mono text-xs"
                              />
                              <span className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 text-xs text-muted-foreground">
                                %
                              </span>
                            </div>
                            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                              +{formatAmountWithUnit(contributionCents(row, cutBps), currency)}
                            </span>
                          </div>
                        </div>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label={`Remove ${row.name}`}
                          onClick={() =>
                            setRows((prev) => prev.filter((other) => other.id !== row.id))
                          }
                        >
                          <XIcon />
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">
            <StepNumber n={5} /> {edit ? "Save" : "Create"} your {terms.box.singular}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div className="grid gap-3">
            {commission && maxCut > 0 ? (
              <div className="grid gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="forge-cut">Your cut</Label>
                  <span className="font-mono text-sm tabular-nums">{cutPercent.toFixed(2)}%</span>
                </div>
                <input
                  id="forge-cut"
                  type="range"
                  min={0}
                  max={maxCut}
                  step={0.01}
                  value={cutPercent}
                  onChange={(e) => setCutPercent(Number(e.target.value))}
                  className="w-full accent-primary"
                />
                <p className="text-xs text-muted-foreground">
                  You earn{" "}
                  <span className="font-medium text-foreground">
                    {formatAmountWithUnit(earningsPerOpenCents(priceCents, cutPercent), currency)}
                  </span>{" "}
                  per open. A higher cut raises the price.
                </p>
              </div>
            ) : null}
          </div>
          <div className="grid gap-2 md:min-w-64">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm text-muted-foreground">
                {capitalizeTerm(terms.box.singular)} price
              </span>
              <span className="text-2xl font-semibold tracking-tight">
                {formatAmountWithUnit(priceCents, currency)}
              </span>
            </div>
            <Button
              size="lg"
              disabled={blocker !== null || pending || (!edit && images.isPending)}
              onClick={submit}
            >
              {pending ? "Saving…" : forgeSubmitLabel(state, terms)}
            </Button>
            <Link
              to={edit ? "/forge/mine" : "/forge"}
              className={buttonVariants({ variant: "ghost", size: "sm" })}
            >
              Cancel
            </Link>
          </div>
        </CardContent>
      </Card>

      {usernameOpen && (
        <UsernameDialog open={usernameOpen} onOpenChange={setUsernameOpen} username={null} />
      )}
    </div>
  );
}

function StepNumber({ n }: { n: number }) {
  return (
    <span className="mr-1 inline-flex size-5 items-center justify-center rounded-full bg-primary/15 text-[11px] font-semibold text-primary">
      {n}
    </span>
  );
}

function Step({
  number,
  title,
  description,
  children,
}: {
  number: number;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">
          <StepNumber n={number} /> {title}
        </CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

type ItemGroup = "all" | "bulk";

/**
 * The item search: a name, a value range typed in the store's currency, and a "Mystery Bulk
 * Cards" filter over the tiers the first page leads with. The grid toggles a pick on click.
 */
function ItemPicker({
  selected,
  onToggle,
}: {
  selected: ReadonlySet<string>;
  onToggle: (item: ForgeItem) => void;
}) {
  const terms = useTerminology();
  const currency = useCurrency();
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [minText, setMinText] = useState("");
  const [maxText, setMaxText] = useState("");
  const [group, setGroup] = useState<ItemGroup>("all");

  // Type, then search: one request per pause rather than one per key.
  useEffect(() => {
    const timer = setTimeout(() => setQ(text), 300);
    return () => clearTimeout(timer);
  }, [text]);

  const minCents = minText.trim() ? parseAmount(minText, currency) : null;
  const maxCents = maxText.trim() ? parseAmount(maxText, currency) : null;
  const rangeInvalid =
    (minText.trim() !== "" && minCents === null) || (maxText.trim() !== "" && maxCents === null);

  const items = useInfiniteQuery({
    ...forgeItemsInfiniteQueryOptions({
      q,
      minCents: minCents ?? undefined,
      maxCents: maxCents ?? undefined,
    }),
    enabled: !rangeInvalid,
  });
  const all = useMemo(() => items.data?.pages.flatMap((page) => page.data) ?? [], [items.data]);
  const bulk = all.filter((item) => item.mysteryBulkCard);
  const rest = all.filter((item) => !item.mysteryBulkCard);
  const canLoadMore = group === "all" && !!items.hasNextPage && !items.isFetchingNextPage;
  const sentinel = useLoadMore<HTMLDivElement>(canLoadMore, () => void items.fetchNextPage());

  return (
    <div className="grid gap-3">
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_7rem_7rem]">
        <Input
          aria-label={`Search ${terms.item.plural}`}
          placeholder={`Search ${terms.item.plural}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Input
          aria-label="Minimum value"
          placeholder="Min value"
          inputMode="decimal"
          value={minText}
          onChange={(e) => setMinText(e.target.value)}
        />
        <Input
          aria-label="Maximum value"
          placeholder="Max value"
          inputMode="decimal"
          value={maxText}
          onChange={(e) => setMaxText(e.target.value)}
        />
      </div>
      <div className="flex gap-1.5">
        <Button
          size="sm"
          variant={group === "all" ? "default" : "secondary"}
          onClick={() => setGroup("all")}
        >
          All {terms.item.plural}
        </Button>
        <Button
          size="sm"
          variant={group === "bulk" ? "default" : "secondary"}
          onClick={() => setGroup("bulk")}
        >
          Mystery Bulk Cards
        </Button>
      </div>

      {rangeInvalid && <p className="text-xs text-destructive">Enter the values as amounts.</p>}
      {items.isError && (
        <ErrorBanner>
          {items.error instanceof Error
            ? items.error.message
            : `failed to load ${terms.item.plural}`}
        </ErrorBanner>
      )}

      <div className="max-h-[36rem] overflow-y-auto pr-1">
        {items.isPending && !rangeInvalid ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
            {["a", "b", "c", "d", "e", "f"].map((id) => (
              <Skeleton key={id} className="aspect-[4/5] rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="grid gap-4">
            {bulk.length > 0 && (
              <ItemGroupGrid
                title="Mystery Bulk Cards"
                items={bulk}
                selected={selected}
                onToggle={onToggle}
              />
            )}
            {group === "all" && rest.length > 0 && (
              <ItemGroupGrid
                title={bulk.length > 0 ? capitalizeTerm(terms.item.plural) : undefined}
                items={rest}
                selected={selected}
                onToggle={onToggle}
              />
            )}
            {!rangeInvalid &&
              items.isSuccess &&
              (group === "bulk" ? bulk.length === 0 : all.length === 0) && (
                <p className="text-sm text-muted-foreground">
                  Nothing matches. Try another search or value range.
                </p>
              )}
            {group === "all" && items.hasNextPage ? (
              <div ref={sentinel} className="flex justify-center">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={items.isFetchingNextPage}
                  onClick={() => void items.fetchNextPage()}
                >
                  {items.isFetchingNextPage ? "Loading…" : "Load more"}
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function ItemGroupGrid({
  title,
  items,
  selected,
  onToggle,
}: {
  title?: string;
  items: ForgeItem[];
  selected: ReadonlySet<string>;
  onToggle: (item: ForgeItem) => void;
}) {
  const currency = useCurrency();
  return (
    <section className="grid gap-2">
      {title && (
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
      )}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
        {items.map((item) => {
          const on = selected.has(item.id);
          return (
            <button
              key={item.id}
              type="button"
              aria-pressed={on}
              onClick={() => onToggle(item)}
              className={`relative grid gap-1.5 rounded-lg border p-2 text-left transition-colors ${
                on ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
              }`}
            >
              <span
                className={`absolute top-2 right-2 flex size-4 items-center justify-center rounded border ${
                  on
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input bg-background"
                }`}
              >
                {on && <CheckIcon className="size-3" />}
              </span>
              <BoxImage
                src={item.imageUrl}
                alt=""
                className="aspect-square w-full rounded object-contain"
              />
              <span className="line-clamp-2 text-xs font-medium">{item.name}</span>
              <span className="font-mono text-[11px] text-muted-foreground">
                {formatAmountWithUnit(item.valueCents, currency)}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
